'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckCircle } from '@sprd/sprd-component-kit/v2';
import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useActivateVoucher } from '@/hooks/useCartHooks';
import { useIsClient } from '@/hooks/useIsClient';
import { isCurrentPromoActivatedAtom, isPromoExpiredAtom, promoConfigAtom } from '@/atoms/promoAtom';
import PromoPopup from '@/components/PromoPopup/PromoPopup';
import { promoConditionForProductType } from '@/utility/promoConditionForProductType';
import { tracker } from '@/utility/tracking/tracker';
type PromoCodeWidgetProps = {
  productTypeId: string;
};

/**
 * Advertises the running promotion's discount for this product type above the PDP price
 * (DEV-233329). The code itself is not shown here — the "Code" link opens the same
 * `PromoPopup` the announcement bar does. Renders nothing when no promotion applies.
 *
 * The popup is mounted locally rather than driven through `openPromoPopupAtom`, because
 * the atom's only listener is `HeaderPromoBanner`, which the "promotion-banner" A/B test
 * can switch off — exactly the case this widget exists for.
 */
export const PromoCodeWidget = t0 => {
  const $ = _c(38);
  const {
    productTypeId
  } = t0;
  const t = useTranslations();
  const promoSlot = useAtomValue(promoConfigAtom);
  const isExpired = useAtomValue(isPromoExpiredAtom);
  const isPromoActivated = useAtomValue(isCurrentPromoActivatedAtom);
  const {
    applyVoucher
  } = useActivateVoucher();
  const isClient = useIsClient();
  const [showPopup, setShowPopup] = useState(false);
  const code = promoSlot?.content["promo-tool"].campaignDetails.code;
  const showsStrikethrough = promoSlot?.content["promo-tool"].elements.strikethroughPrices;
  let t1;
  if ($[0] !== productTypeId || $[1] !== promoSlot) {
    t1 = promoConditionForProductType(promoSlot, productTypeId);
    $[0] = productTypeId;
    $[1] = promoSlot;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const condition = t1;
  const isActivated = isClient && isPromoActivated;
  const isVisible = Boolean(!isExpired && code && showsStrikethrough && condition);
  let t2;
  let t3;
  if ($[3] !== code || $[4] !== condition || $[5] !== isVisible || $[6] !== productTypeId) {
    t2 = () => {
      if (!isVisible) {
        return;
      }
      tracker.trackFeature({
        featureUsed: "promo-widget-shown",
        detail: {
          productTypeId,
          discount: condition.value
        },
        promocode: code
      });
    };
    t3 = [isVisible, productTypeId, condition, code];
    $[3] = code;
    $[4] = condition;
    $[5] = isVisible;
    $[6] = productTypeId;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (!isVisible) {
    return null;
  }
  const {
    value: discount,
    restrictions
  } = condition;
  const labelKey = restrictions.productTypes?.length ? "promo.pdpWidget.labelRestricted" : "promo.pdpWidget.label";
  let t4;
  if ($[9] !== code || $[10] !== discount || $[11] !== productTypeId) {
    t4 = () => {
      setShowPopup(true);
      tracker.trackFeature({
        featureUsed: "promo-widget-open",
        detail: {
          productTypeId,
          discount
        },
        promocode: code
      });
    };
    $[9] = code;
    $[10] = discount;
    $[11] = productTypeId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleOpen = t4;
  let t5;
  if ($[13] !== discount || $[14] !== labelKey || $[15] !== t) {
    t5 = t(labelKey, {
      discount
    });
    $[13] = discount;
    $[14] = labelKey;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <span className="font-semibold">{t5}</span>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== isActivated) {
    t7 = isActivated && <CheckCircle className="size-4 shrink-0" aria-hidden="true" />;
    $[19] = isActivated;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== isActivated || $[22] !== t) {
    t8 = isActivated ? t("promo.codeActivated") : t("promo.pdpWidget.code");
    $[21] = isActivated;
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== handleOpen || $[25] !== t7 || $[26] !== t8) {
    t9 = <button type="button" onClick={handleOpen} className="flex cursor-pointer items-center gap-1 whitespace-nowrap underline" data-tracking="EVENT - promo widget open">{t7}{t8}</button>;
    $[24] = handleOpen;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t6 || $[29] !== t9) {
    t10 = <p className="flex w-fit flex-wrap items-center gap-x-2 gap-y-0.5 rounded bg-black px-2 py-1 text-sm text-white">{t6}{t9}</p>;
    $[28] = t6;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setShowPopup(false);
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== applyVoucher || $[33] !== showPopup) {
    t12 = <PromoPopup open={showPopup} onClose={t11} applyVoucher={applyVoucher} />;
    $[32] = applyVoucher;
    $[33] = showPopup;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t12) {
    t13 = <>{t10}{t12}</>;
    $[35] = t10;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};